import { Map } from 'immutable';
import { createSelector } from 'reselect';
import { fasteningTypes } from '../generators/sinks';
import { planSelector, useCoverPanelsSelector } from '../selectors';
import * as planParameters from '../planParameters';
import parameterSelector from './utils/parameterSelector';
import { NO_KITCHEN_ISLAND_ID } from '../generators/kitchenIslands';
import { NO_WORKTOP_MATERIAL } from '../generators/materials';
import { NONE } from '../generators/splashbacks';

export const materialParameterSelector = parameterSelector(planParameters.material);
export const expressionParameterSelector = parameterSelector(planParameters.expression);
export const coverPanelParameterSelector = parameterSelector(planParameters.coverPanel);
export const thicknessParameterSelector = parameterSelector(planParameters.thickness);
export const splashbackParameterSelector = parameterSelector(planParameters.splashback);
export const wallEdgingStripCustomizableParameterSelector = parameterSelector(planParameters.wallEdgingStripCustomizable);
export const wallPanelMaterialParameterSelector = parameterSelector(planParameters.wallPanelMaterial);
export const wallEdgingStripMaterialParameterSelector = parameterSelector(planParameters.wallEdgingStripMaterial);
export const wallEdgingStripMaterialCustomizableParameterSelector = parameterSelector(planParameters.wallEdgingStripCustomizableMaterial);
export const wallPanelExpressionParameterSelector = parameterSelector(planParameters.wallPanelExpression);
export const edgeParameterSelector = parameterSelector(planParameters.edge);
export const wallLocationParameterSelector = parameterSelector(planParameters.wallLocation);
export const kitchenIslandWallLocationParameterSelector = parameterSelector(planParameters.kitchenIslandWallLocation);
export const kitchenIslandParameterSelector = parameterSelector(planParameters.kitchenIsland);
export const kitchenIslandDoubleSidedSelector = parameterSelector(planParameters.kitchenIslandDoubleSided);
export const kitchenIslandSplashbackSelector = parameterSelector(planParameters.kitchenIslandSplashBack);
export const doubleSidedSelector = parameterSelector(planParameters.doubleSided);
export const overhangParameterSelector = parameterSelector(planParameters.overhang);
export const overhangSideParameterSelector = parameterSelector(planParameters.overhangSide);
export const sinkMaterialParameterSelector = parameterSelector(planParameters.sinkMaterial);
export const sinkModelParameterSelector = parameterSelector(planParameters.sinkModel);
export const sinkFasteningParameterSelector = parameterSelector(planParameters.sinkFastening);
export const drainerGroovesParameterSelector = parameterSelector(planParameters.drainerGrooves);

export const kitchenIslandMaterialParameterSelector = parameterSelector(planParameters.kitchenIslandMaterial);
export const isKitchenIslandMaterialUsedSelector = createSelector(
    kitchenIslandMaterialParameterSelector,
    (material) => material.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL
);
export const kitchenIslandExpressionParameterSelector = parameterSelector(planParameters.kitchenIslandExpression);
export const kitchenIslandCoverPanelParameterSelector = parameterSelector(planParameters.kitchenIslandCoverPanel);
export const kitchenIslandEdgeParameterSelector = parameterSelector(planParameters.kitchenIslandEdge);
export const kitchenIslandThicknessParameterSelector = parameterSelector(planParameters.kitchenIslandThickness);
export const kitchenIslandOverhangParameterSelector = parameterSelector(planParameters.kitchenIslandOverhang);
export const kitchenIslandOverhangSideParameterSelector = parameterSelector(planParameters.kitchenIslandOverhangSide);

export const plannedShapeParameterSelector = createSelector(
  planSelector,
  (plan) => {
    const plannedShape = plan.get(planParameters.plannedShape);

    let measurements;
    if (plannedShape.get('value')) {
      measurements = plannedShape.getIn(['value', 'dimensions']).map((dimension, dimensionKey) => {
        if (dimension.get('formula')) {
          return dimension.get('formula')(plannedShape.get('measurements'));
        }

        return plannedShape.getIn(['measurements', dimensionKey]);
      });
    } else {
      measurements = new Map();
    }

    return plannedShape.set('measurements', measurements);
  }
);

export const isMaterialUsedSelector = createSelector(
  materialParameterSelector,
  (material) => material.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL
);

export const kitchenIslandWithMeasurementsSelector = createSelector(
  planSelector,
  (plan) => {
    const kitchenIsland = plan.get(planParameters.kitchenIsland);
    let measurements;
    if (kitchenIsland.get('value') && kitchenIsland.getIn(['value', 'id']) !== NO_KITCHEN_ISLAND_ID) {
      measurements = kitchenIsland.getIn(['value', 'dimensions']).map((dimension, dimensionKey) => {
        return kitchenIsland.getIn(['measurements', dimensionKey]) || dimension.get('defaultValue');
      });
    } else {
      measurements = new Map();
    }

    return kitchenIsland.set('measurements', measurements);
  }
);

export const shapeFlippedSelector = createSelector(
  plannedShapeParameterSelector,
  (plannedShape) => plannedShape.get('flipped'),
);

export const kitchenIslandFlipped = createSelector(
  kitchenIslandWithMeasurementsSelector,
  (kitchenIsland) => kitchenIsland.get('flipped'),
);

const makeSelectedThicknessSelector = (expressionSelector, thicknessSelector) => createSelector(
  expressionSelector,
  thicknessSelector,
  (expressionParameter, thicknessParameter) => {
    if (!thicknessParameter.get('value')) return null;
    if (!expressionParameter.get('value')) return null;
    return expressionParameter.getIn(['value', 'thicknesses'])
    .find((thickness) => thickness.get('id') === thicknessParameter.getIn(['value', 'id']));
  }
);


const makeSelectedKitchenIslandThicknessSelector = (expressionSelector, thicknessSelector) => createSelector(
  expressionSelector,
  thicknessSelector,
  (expressionParameter, thicknessParameter) => {
    if (!thicknessParameter.get('value')) return null;
    if (!expressionParameter.get('value')) return null;
    return expressionParameter.getIn(['value', 'thicknesses'])
    .find((thickness) => thickness.get('id') === thicknessParameter.getIn(['value', 'id']));
  }
);

const makeSelectedCoverPanelThicknessSelector = (coverPanelSelector, thicknessSelector, useCoverPanels) => createSelector(
  coverPanelSelector,
  thicknessSelector,
  useCoverPanels,
  (coverPanelParameter, thicknessParameter, useCoverPanels) => {
    if(!useCoverPanels){
      return null;
    }
    if (!thicknessParameter.get('value')) return null;
    if (!coverPanelParameter.get('value')) return null;
    return coverPanelParameter.getIn(['value', 'thicknesses'])
    .find((thickness) => thickness.get('id') === thicknessParameter.getIn(['value', 'id']));
  }
);

const makeSelectedKitchenIslandCoverPanelThicknessSelector = (coverPanelSelector, thicknessSelector, useCoverPanels) => createSelector(
  coverPanelSelector,
  thicknessSelector,
  useCoverPanels,
  (coverPanelParameter, thicknessParameter, useCoverPanels) => {
    if(!useCoverPanels){
      return null;
    }
    if (!thicknessParameter.get('value')) return null;
    if (!coverPanelParameter.get('value')) return null;
    return coverPanelParameter.getIn(['value', 'thicknesses'])
    .find((thickness) => thickness.get('id') === thicknessParameter.getIn(['value', 'id']));
  }
);


export const selectedCoverPanelThicknessSelector = makeSelectedCoverPanelThicknessSelector(coverPanelParameterSelector, thicknessParameterSelector, useCoverPanelsSelector);
export const selectedKitchenIslandCoverPanelThicknessSelector = makeSelectedKitchenIslandCoverPanelThicknessSelector(kitchenIslandCoverPanelParameterSelector, kitchenIslandThicknessParameterSelector, useCoverPanelsSelector);


export const selectedThicknessSelector = makeSelectedThicknessSelector(expressionParameterSelector, thicknessParameterSelector);
export const selectedKitchenIslandThicknessSelector = makeSelectedKitchenIslandThicknessSelector(kitchenIslandExpressionParameterSelector, kitchenIslandThicknessParameterSelector);

const makeSelectedEdgeSelector = (materialSelector, thicknessSelector, edgeSelector) => createSelector(
  materialSelector,
  thicknessSelector,
  edgeSelector,
  (materialParameter, selectedThickness, edgeParameter) => {
    if(materialParameter.getIn(['value', 'edges']) === 'noEdgeIncluded'){
      return null;
    }
    if (selectedThickness && materialParameter.get('value')) {
      const param = { thickness: selectedThickness.get('thickness') };
      return materialParameter.getIn(['value', 'edges'])
      .find((edge) => edge.get('value') === edgeParameter.getIn(['value', 'id']) && edge.get('conditions').every((cond) => cond(param)));
    } else {
      return null;
    }
  },
);

export const selectedEdgeSelector = makeSelectedEdgeSelector(materialParameterSelector, selectedThicknessSelector, edgeParameterSelector);
export const selectedKitchenIslandEdgeSelector = makeSelectedEdgeSelector(kitchenIslandMaterialParameterSelector, selectedKitchenIslandThicknessSelector, kitchenIslandEdgeParameterSelector);

export const expressionSelectionsSelector = createSelector(
  planSelector,
  (plan) => plan.get('expressionSelections'),
);

export const coverPanelSelectionsSelector = createSelector(
  planSelector,
  (plan) => plan.get('coverPanelSelections'),
);

export const kitchenIslandExpressionSelectionsSelector = createSelector(
  planSelector,
  (plan) => plan.get('kitchenIslandExpressionSelections'),
);

export const kitchenIslandCoverPanelSelectionsSelector = createSelector(
  planSelector,
  (plan) => plan.get('kitchenIslandCoverPanelSelections'),
);

export const hasChangesSelector = createSelector(
  planSelector,
  (plan) => plan.get('hasChanges')
);

export const wallPanelExpressionSelectionsSelector = createSelector(
  planSelector,
  (plan) => plan.get('wallPanelExpressionSelections')
);

export const sinkModelDepthConditionSelector = createSelector(
  sinkModelParameterSelector,
  (sinkModel) => sinkModel && sinkModel.getIn(['value', 'configuration', 'depthCondition'])
);


function getSink(fasteningType, fastenings, worktopMaterialId) {
  switch (fasteningType) {
    case fasteningTypes.topMounted:
      return fastenings.get(0);
    case fasteningTypes.underGlued:
      return fastenings.find((fastening) => fastening.get('worktopMaterialId') === worktopMaterialId);
    case fasteningTypes.fitBetween:
      return fastenings.get(0);
    default:
      return undefined;
  }
}

export const sinkSelector = createSelector(
  materialParameterSelector,
  sinkMaterialParameterSelector,
  sinkModelParameterSelector,
  sinkFasteningParameterSelector,
  (worktopMaterial, sinkMaterial, sinkModel, sinkFastening) => {
    if (sinkMaterial.getIn(['value', 'id']) === 'noSink') {
      return null;
    }
    const useDecentralizedSinkOption = worktopMaterial.getIn(['value', 'decentralizedSinkOptions']);
    let fasteningType = sinkFastening.getIn(['value', 'id']);
    if (sinkModel.getIn(['value', 'isManuallyConfigured']) !== undefined && sinkModel.getIn(['value', 'isManuallyConfigured']) !== null) {
      if (useDecentralizedSinkOption && fasteningType === 'underGlued' && !sinkModel.getIn(['value', 'isManuallyConfigured'])) {
        fasteningType = 'topMounted';
      }
    }
    const fastenings = sinkModel.getIn(['value', 'fastenings', fasteningType]);

    if (fastenings) {
      const worktopMaterialId = worktopMaterial.getIn(['value', 'id']);
      return getSink(fasteningType, fastenings, worktopMaterialId);
    }

    return null;  //need to return null to be able to return empty obj to a sink card, if sink is not availble to a fastnening combination
  }
);

export const sinkUISelector = createSelector(
  materialParameterSelector,
  sinkMaterialParameterSelector,
  sinkFasteningParameterSelector,
  (worktopMaterial, sinkMaterial, sinkFastening) => {
    if (sinkMaterial.getIn(['value', 'id']) === 'noSink') {
      return null;
    }
    const sinksUI = [];
    const sinkModels = sinkMaterial.getIn(['value', 'models']);
    sinkModels.map(sinkModel => {
    const useDecentralizedSinkOption = worktopMaterial.getIn(['value', 'decentralizedSinkOptions']);
    let fasteningType = sinkFastening.getIn(['value', 'id']);
    if (sinkModel.getIn(['isManuallyConfigured']) !== undefined && sinkModel.getIn(['isManuallyConfigured']) !== null) {
      if (useDecentralizedSinkOption && fasteningType === 'underGlued' && !sinkModel.getIn(['isManuallyConfigured'])) {
        fasteningType = 'topMounted';
      }
    }
    const fastenings = sinkModel.getIn(['fastenings', fasteningType]);

    if (fastenings) {
      const worktopMaterialId = worktopMaterial.getIn(['value', 'id']);
      sinksUI.push(getSink(fasteningType, fastenings, worktopMaterialId));
    } else {
      sinksUI.push(null);
    }
  });
  return sinksUI;
  }
);

export const isExpressionDecentralizedSelector = createSelector(
  selectedThicknessSelector,
  (selectedThickness) => {
    if (selectedThickness === null || selectedThickness === undefined) {
      return null;
    }
    return selectedThickness.get('priceByArea');
  }
);

export const isKitchenIslandExpressionDecentralizedSelector = createSelector(
  selectedKitchenIslandThicknessSelector,
  (selectedThickness) => {
    if (selectedThickness === null || selectedThickness === undefined) {
      return null;
    }
    return selectedThickness.get('priceByArea');
  }
);
